import CloudUploadIcon from '@components/Icons/CloudUploadIcon';
import { formatRelativeTime } from '@lib/utils/relativeTime';
import { formatBytesPerSec, formatDurationToString } from '@lib/utils/units';
import {
  CheckCircleOutlineRounded,
  CheckCircleRounded,
  CloseRounded,
  ErrorRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { GenericToastNotification, Notification, notificationStore } from '@stores/Notifications';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { BatchCardLive } from './BatchCardLive';
import { deriveBatchState } from './batchState';
import { RichEventCard } from './RichEventCard';
import { deriveActiveUploadPhase } from './uploadPhase';
import css from './UploadStatus.module.scss';
import { UploadPanelHandlers } from './useUploadActivity';

function VariantIcon({ variant }: { variant: GenericToastNotification['variant'] }) {
  return variant === 'success' ? <CheckCircleRounded /> : <ErrorRounded />;
}

function GenericToastCard({
  n,
  onDismiss,
}: {
  n: GenericToastNotification;
  onDismiss: () => void;
}) {
  return (
    <div className={css.genericItem}>
      <div className={css.genericIcon} data-variant={n.variant}>
        <VariantIcon variant={n.variant} />
      </div>
      <div className={css.genericBody}>
        <div className={css.genericTitle}>{n.header}</div>
        {n.description && <div className={css.genericDesc}>{n.description}</div>}
        <div className={css.genericTime}>{formatRelativeTime(n.timestamp)}</div>
      </div>
      <button className={css.genericDismiss} onClick={onDismiss} aria-label="Dismiss notification">
        <CloseRounded />
      </button>
    </div>
  );
}

const NotificationCard = observer(function NotificationCard({
  notification,
  onDismiss,
  onOpenResults,
  onRetryFailed,
  onDeleteBatch,
  onCancelBatch,
}: {
  notification: Notification;
  onDismiss: (n: Notification) => void;
  onOpenResults: (notificationId: string) => void;
  onRetryFailed: (notificationId: string) => void;
  onDeleteBatch: (notificationId: string) => void;
  onCancelBatch: (notificationId: string) => void;
}) {
  // Stabilise the bound dismiss so React.memo on the dumb BatchCard can
  // bail on re-render when the parent's MobX tick wakes us up but
  // nothing on the VM has actually shifted. Without this, the inline
  // `() => onDismiss(notification)` arrow had a fresh identity every
  // render, defeating the memo for the entire 500ms tick cadence.
  const boundDismiss = useCallback(() => onDismiss(notification), [onDismiss, notification]);
  if (notification.type === 'generic-toast') {
    return <GenericToastCard n={notification} onDismiss={boundDismiss} />;
  }
  if (notification.type === 'rich-event') {
    return (
      <RichEventCard
        notification={notification}
        onDismiss={boundDismiss}
        formatTime={formatRelativeTime}
      />
    );
  }
  if (notification.type === 'notif-bell-entry') {
    // notif-bell entries belong to the separate Notifications surface — they
    // should never reach the upload panel via the standard iteration. If
    // one does (e.g. a future caller forgets to filter), render nothing
    // rather than blowing up the panel.
    return null;
  }
  return (
    <BatchCardLive
      notification={notification}
      onDismiss={boundDismiss}
      formatTime={formatRelativeTime}
      onOpenResults={onOpenResults}
      onRetryFailed={onRetryFailed}
      onDeleteBatch={onDeleteBatch}
      onCancelBatch={onCancelBatch}
    />
  );
});

type GroupKey = 'inProgress' | 'today' | 'yesterday' | 'older';

const GROUP_LABELS: Record<GroupKey, string> = {
  inProgress: 'In progress',
  today: 'Today',
  yesterday: 'Yesterday',
  older: 'Older',
};

export function isNotifActive(n: Notification): boolean {
  if (n.type === 'add-media-via-url') return n.progress < 1;
  if (n.type === 'upload-file') return n.uploadProgress < 1 || n.processingProgress < 1;
  return false;
}

function groupOf(n: Notification, now: number): GroupKey {
  if (isNotifActive(n)) return 'inProgress';
  const t = n.timestamp;
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  if (t >= startOfToday.getTime()) return 'today';
  if (t >= startOfYesterday.getTime()) return 'yesterday';
  return 'older';
}

interface UploadPanelBodyProps {
  handlers: UploadPanelHandlers;
  /** Fill the container (mobile drawer) instead of the fixed popover width. */
  fullWidth?: boolean;
  /** Render slot for debug-only simulated `<BatchCard>` mounts. When
   * provided, the panel adds a "Simulations" group at the top of its
   * card list — above any real notification groups — and the empty
   * state is suppressed. Used by `ToastDebugPage`. Omit for production. */
  simulationCards?: ReactNode;
}

/**
 * The upload-activity panel body: warn/success banner, grouped card list, and
 * footer. Shell-agnostic — rendered inside the desktop popover AND the mobile
 * drawer's Uploads tab. Only mounts while the surface is open, so it owns its
 * own 30s tick for relative-time refresh unconditionally.
 */
export const UploadPanelBody = observer(function UploadPanelBody({
  handlers,
  fullWidth,
  simulationCards,
}: UploadPanelBodyProps) {
  const uploadManager = useUploadManager();
  const { onDismiss, onOpenResults, onRetryFailed, onDeleteBatch, onCancelBatch } = handlers;

  // Tick every 30s so timestamps refresh while the panel is on screen.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // The upload-activity panel only shows upload-related entries. notif-bell
  // entries land in the separate Notifications surface and would otherwise
  // bucket under "Today" here purely by timestamp.
  const notifications = notificationStore.sorted.filter((n) => n.type !== 'notif-bell-entry');

  // ─── Warn banner state machine (see batchState.ts) ──────────────────────
  const batchInfos = notifications
    .filter((n) => n.type === 'add-media-via-url' || n.type === 'upload-file')
    .map((n) => ({ type: n.type, state: deriveBatchState(n, uploadManager) }));
  const anyUploading = batchInfos.some((b) => b.state === 'uploading');
  const anyStalled = batchInfos.some((b) => b.state === 'stalled');
  const anyWrappingUp = batchInfos.some((b) => b.state === 'wrapping-up');
  const anyPreparing = batchInfos.some((b) => b.state === 'preparing');
  const anyQueued = batchInfos.some((b) => b.state === 'queued');
  // Green "uploads done, processing in background" banner only after the user
  // has actually gone through an upload phase — gating on `type: 'upload-file'`
  // excludes URL imports, which never show the red warning so don't need a
  // green transition out of it either.
  const anyProcessingUpload = batchInfos.some(
    (b) => b.type === 'upload-file' && b.state === 'processing',
  );

  // Collapse the batch set into one active phase (fixed priority — an in-flight
  // transfer always beats queued/prepare/wrap-up) and map it with an EXHAUSTIVE
  // switch, so a phase can never silently fall through to the wrong subtitle
  // (e.g. "Queued for upload…" while bytes are moving). See uploadPhase.ts.
  const uploadPhase = deriveActiveUploadPhase({
    uploading: anyUploading,
    stalled: anyStalled,
    wrappingUp: anyWrappingUp,
    preparing: anyPreparing,
    queued: anyQueued,
    processing: anyProcessingUpload,
  });
  const showActiveWarning =
    uploadPhase === 'uploading' ||
    uploadPhase === 'stalled' ||
    uploadPhase === 'wrapping-up' ||
    uploadPhase === 'preparing' ||
    uploadPhase === 'queued';
  const showProcessingNotice = uploadPhase === 'processing';
  const eta = uploadManager.globalUploadEta;
  const speedBps = uploadManager.globalUploadSpeedBps;
  const statsLine: string = (() => {
    switch (uploadPhase) {
      case 'uploading':
        return eta.kind === 'estimate' && eta.seconds > 0 && speedBps > 0
          ? `About ${formatDurationToString(eta.seconds, { detail: 2 })} left at ${formatBytesPerSec(speedBps)}`
          : 'Uploading…';
      // Bytes moved and then stopped. The banner is the one place the user looks
      // to find out why nothing is happening, so this must not reuse either
      // "Uploading…" (hides the problem) or "Preparing files for upload…"
      // (actively wrong — the transfer is live and mid-flight).
      case 'stalled':
        return 'Waiting for a stable connection…';
      case 'wrapping-up':
        return 'Wrapping up uploads…';
      case 'preparing':
        return 'Preparing files for upload…';
      case 'queued':
        return 'Queued for upload…';
      case 'processing':
      case 'idle':
        // Banner isn't rendered in these phases (see showActiveWarning), so no
        // subtitle is needed.
        return '';
      default: {
        const _exhaustive: never = uploadPhase;
        return _exhaustive;
      }
    }
  })();
  // Sum media across all batch notifications so the footer shows the count
  // users actually care about ("3 items") instead of "1 batch" / "2 batches".
  const itemCount = notifications.reduce(
    (sum, n) =>
      n.type === 'add-media-via-url' || n.type === 'upload-file' ? sum + n.mediaTitles.length : sum,
    0,
  );

  // Not memoised: `notifications` is the observable-array reference, which
  // doesn't change when individual notification fields mutate (e.g.
  // `processingProgress` flipping to 1). Re-computing fresh each render keeps
  // grouping in lock-step with the underlying observable state.
  const groups: Record<GroupKey, Notification[]> = {
    inProgress: [],
    today: [],
    yesterday: [],
    older: [],
  };
  {
    const now = Date.now();
    for (const n of notifications) {
      groups[groupOf(n, now)].push(n);
    }
  }

  const orderedGroupKeys: GroupKey[] = ['inProgress', 'today', 'yesterday', 'older'];

  return (
    <div className={classNames(css.panel, fullWidth && css.panelDrawer)}>
      <div className={css.panelHead}>
        <div className={css.panelHeadRow}>
          <span className={css.panelTitleGroup}>
            <span className={css.panelTitle}>Upload status</span>
            <span className={css.betaBadge}>Beta</span>
          </span>
          <button
            className={css.clearBtn}
            onClick={() => notificationStore.clearCompleted()}
            // Disabled when nothing is clearable — either the panel is empty,
            // or everything in it is still uploading / processing.
            disabled={!notifications.some((n) => !isNotifActive(n))}
          >
            Clear all
          </button>
        </div>
        {showActiveWarning && (
          <div className={css.warnBanner}>
            <WarningAmberRounded />
            <div className={css.bannerText}>
              <div className={css.bannerHeadline}>Keep this tab open until uploads finish</div>
              {statsLine && <div className={css.warnBannerStat}>{statsLine}</div>}
            </div>
          </div>
        )}
        {showProcessingNotice && (
          <div className={css.successBanner}>
            <CheckCircleOutlineRounded />
            <div className={css.bannerText}>
              <div className={css.bannerHeadline}>Uploads complete — safe to close this tab</div>
              <div className={css.successBannerStat}>Processing in the background</div>
            </div>
          </div>
        )}
      </div>
      <div className={css.panelList}>
        {simulationCards && (
          <div>
            <div className={`${css.groupHeader} ${css.groupHeaderInProgress}`}>
              <span className={css.groupDot} />
              Simulations (debug)
            </div>
            {simulationCards}
          </div>
        )}
        {notifications.length === 0 && !simulationCards ? (
          <div className={css.empty}>
            <CloudUploadIcon />
            <div className={css.emptyTitle}>No uploads in progress</div>
            <div className={css.emptySub}>Drag a file or paste a link to get started</div>
          </div>
        ) : notifications.length === 0 ? null : (
          orderedGroupKeys.map((key) => {
            const items = groups[key];
            if (items.length === 0) return null;
            return (
              <div key={key}>
                <div
                  className={`${css.groupHeader} ${
                    key === 'inProgress' ? css.groupHeaderInProgress : ''
                  }`}
                >
                  {key === 'inProgress' && <span className={css.groupDot} />}
                  {GROUP_LABELS[key]}
                </div>
                {items.map((n) => (
                  <NotificationCard
                    key={n.id}
                    notification={n}
                    onDismiss={onDismiss}
                    onOpenResults={onOpenResults}
                    onRetryFailed={onRetryFailed}
                    onDeleteBatch={onDeleteBatch}
                    onCancelBatch={onCancelBatch}
                  />
                ))}
              </div>
            );
          })
        )}
      </div>
      <div className={css.panelFoot}>
        {itemCount > 0 && (
          <span className={css.summary}>
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
        )}
        <button className={css.viewResultsLink} onClick={() => onOpenResults(undefined)}>
          View all results
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
});
